import React, { FC, useEffect, useState } from "react";
import PopupBackground from "@component/PopupBackground";
import styled from "styled-components";
import { useParams } from "react-router-dom";
import { Form, Input, Button, InputNumber } from "antd";
import { useAppDispatch } from "src/store";
import {
  RetailerProductDetail,
  CreateRetailerProductParam,
  UpdateRetailerProductParam,
  Currency,
} from "@api/supplyChainApi";
import { createRetailerProduct, updateRetailerProduct } from "@redux/supplyChainSlice";
import brandOperationApi from "@api/brandOperationApi";
import { PopupStateEnum } from "@constant/PopupState";
import { DeductionType } from "@api/throughShipment/throughShipmentApi";
import { dealerState, fetchDeductionFeeList } from "@redux/dealerSlice";
import { useSelector } from "react-redux";

interface Props {
  onClose: () => void;
  currentData: RetailerProductDetail | null;
  mode?: PopupStateEnum;
  retailerId?: number;
}

const PopWrapper = styled.div`
  background: ${({ theme }) => theme.colorNeutral100};
  border-radius: 2px;
  width: 620px;
  padding: 23px 14px 20px 20px;
  .ant-form-item-label > label.ant-form-item-required:not(.ant-form-item-required-mark-optional)::before {
    order: 1;
  }
  .ant-form-item-label > label.ant-form-item-required:not(.ant-form-item-required-mark-optional)::after {
    margin: 0;
  }
`;
const Row = styled.div`
  display: flex;
`;
const RowTitle = styled.div<{ width: number }>`
  width: ${({ width }) => width}px;
  align-self: flex-start;
  text-align: left;
  margin-right: 15px;
  line-height: 32px;
  font-size: 14px;
  white-space: nowrap;
`;
const RequiredIcon = styled.span`
  color: ${({ theme }) => theme.colorSecondary500};
`;
const RowContent = styled.div<{ width: number }>`
  width: ${({ width }) => width}px;
`;
const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;
const CancelButton = styled(Button)`
  margin-right: 8px;
`;
const ErrorText = styled.p`
  color: #f16c5d;
  padding: 10px 0;
`;
const ATag = styled.a`
  cursor: pointer;
  text-decoration: underline;
  padding: 0 5px;
  display: inline;
`;

const SupplyChainProductPopup: FC<Props> = (props) => {
  const { onClose, currentData, mode, retailerId } = props;
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const { supplyChainRelationId, currencyNumber } = useParams();
  const nameForm = Form.useWatch("name", form);
  const skuForm = Form.useWatch("sku", form);
  const [errMsg, setErrMsg] = useState<string>("");
  const [linkUrl, setLinkUrl] = useState<string | null>();

  const isEditMode = mode === PopupStateEnum.EDIT && !!currentData;
  const checkedSku = mode === PopupStateEnum.CREATE ? !!(nameForm && skuForm) : true;

  const {
    deductionFeeList: { results: retailerDeductionList },
  } = useSelector(dealerState);
  const getDeductionByType = (type: DeductionType) => retailerDeductionList.find((item) => item.deductionType === type);

  const isPostDeductedByRetailer = getDeductionByType(DeductionType.POST_DEDUCTION)?.isActive;
  const isPreDeductedByRetailer = getDeductionByType(DeductionType.PRE_DEDUCTION)?.isActive;

  const currencyTypeMap: Partial<Record<Currency, string>> = {
    [Currency.TWD]: "TWD",
    [Currency.USD]: "USD",
    [Currency.RMB]: "RMB",
  };
  const currencyType = currencyTypeMap[Number(currencyNumber) as Currency] || "TWD";

  const handleOnSubmit = () => {
    const formValues = form.getFieldsValue();
    const baseParams = {
      sku: formValues.sku,
      no: formValues.no,
      name: formValues.name,
      nameEng: formValues.nameEng,
      price: Number(formValues.price),
      cost: Number(formValues.cost),
      quotedPrice: Number(formValues.quotedPrice),
      // 若使用者未輸入，系統自動帶入1
      canAcceptDays: formValues.canAcceptDays != null ? Number(formValues.canAcceptDays) : 1,
      pcsPerCarton: formValues.pcsPerCarton != null ? Number(formValues.pcsPerCarton) : 1,
      supplyChainRetailerProductlineRelation: Number(supplyChainRelationId),
      skuPlatform: formValues.skuPlatform,
    };
    if (currentData) {
      const params: UpdateRetailerProductParam = {
        ...baseParams,
        id: currentData.id,
        currency: currentData.currency,
        postDeductionRate: formValues.postDeductionRate,
        preDeductionRate: formValues.preDeductionRate,
      };
      dispatch(updateRetailerProduct(params));
    } else {
      const params: CreateRetailerProductParam = {
        ...baseParams,
        postDeductionRate: isPostDeductedByRetailer ? undefined : formValues.postDeductionRate,
        preDeductionRate: isPreDeductedByRetailer ? undefined : formValues.preDeductionRate,
      };
      dispatch(createRetailerProduct(params));
    }
    onClose();
  };

  const checkProductSku = async () => {
    if (!skuForm) return;
    setLinkUrl(null);
    setErrMsg("");
    try {
      const response = await brandOperationApi.fetchCheckProductSku({ sku: skuForm, supplyChainRelationId });
      form.setFieldValue("name", response.name);
    } catch (e: any) {
      const errorMessage = e.message;
      const hasLinkUrl = e.statusCode === 8001 && e.result?.[0];

      setErrMsg(errorMessage);
      setLinkUrl(hasLinkUrl ? e.result[0] : null);
    }
  };

  const handleOnChangeSku = () => {
    setErrMsg("");
  };

  // retailerList改變時,取得retailerDeduction的資料
  useEffect(() => {
    if (retailerId) dispatch(fetchDeductionFeeList({ retailer: retailerId }));
  }, [dispatch, retailerId]);

  // retailerDeductionList改變時,設定前抽後扣的初始值 (如果是新增模式才設定,編輯模式的初始值由currentData帶入)
  useEffect(() => {
    if (retailerDeductionList.length > 0 && mode === PopupStateEnum.CREATE) {
      const deductionRates = retailerDeductionList.reduce((acc, item) => {
        if (item.deductionType === DeductionType.POST_DEDUCTION) {
          acc.postDeductionRate = item.rate;
        } else if (item.deductionType === DeductionType.PRE_DEDUCTION) {
          acc.preDeductionRate = item.rate;
        }
        return acc;
      }, {} as { postDeductionRate?: number; preDeductionRate?: number });

      form.setFieldsValue(deductionRates);
    }
  }, [form, mode, retailerDeductionList]);

  // 編輯初始資料
  useEffect(() => {
    if (mode === PopupStateEnum.EDIT && currentData) {
      form.setFieldsValue({
        ...currentData,
        preDeductionRate: currentData.preDeductionRate * 100,
        postDeductionRate: currentData.postDeductionRate * 100,
      });
    }
  }, [currentData, form, mode]);

  return (
    <PopupBackground close={onClose} fixed>
      <PopWrapper>
        <Form initialValues={{ ...currentData }} form={form} onFinish={handleOnSubmit}>
          <Row>
            <RowTitle width={100}>
              SKU(品號)
              <RequiredIcon>*</RequiredIcon>
            </RowTitle>
            <div>
              <Row>
                <RowContent width={275}>
                  <Form.Item noStyle name="sku" rules={[{ required: true, message: "" }]}>
                    <Input onChange={handleOnChangeSku} disabled={isEditMode} />
                  </Form.Item>
                </RowContent>
                <Button onClick={checkProductSku} type="primary" disabled={isEditMode}>
                  驗證SKU(品號)
                </Button>
              </Row>
              <ErrorText>
                {linkUrl ? (
                  <>
                    商品尚未建立通路為【品運】的合作方式，請至
                    <ATag href={`${linkUrl}`} target="_blank" rel="noreferrer">
                      {"{商品基本資訊}"}
                    </ATag>
                    新增
                  </>
                ) : (
                  errMsg
                )}
              </ErrorText>
            </div>
          </Row>
          <Row>
            <RowTitle width={100}>
              品名
              <RequiredIcon>*</RequiredIcon>
            </RowTitle>
            <RowContent width={400}>
              <Form.Item name="name" rules={[{ required: true, message: "" }]}>
                <Input disabled placeholder="驗證完成 SKU(品號)後，系統會自動帶入品名" />
              </Form.Item>
            </RowContent>
          </Row>
          <Row>
            <RowTitle width={100}>
              商品售價(TWD)
              <RequiredIcon>*</RequiredIcon>
            </RowTitle>
            <RowContent width={400}>
              <Form.Item name="price" rules={[{ required: true, message: "" }]}>
                <Input placeholder="驗證完成 SKU(品號)後才能輸入" disabled={!isEditMode && !checkedSku} />
              </Form.Item>
            </RowContent>
          </Row>
          <Row>
            <RowTitle width={100}>
              商品前抽(%)
              {!isPreDeductedByRetailer && <RequiredIcon>*</RequiredIcon>}
            </RowTitle>
            <RowContent width={400}>
              <Form.Item name="preDeductionRate" rules={[{ required: !isPreDeductedByRetailer, message: "" }]}>
                <InputNumber
                  addonAfter="%"
                  min={0}
                  max={100}
                  step={0.01}
                  precision={3}
                  disabled={isPreDeductedByRetailer}
                />
              </Form.Item>
            </RowContent>
          </Row>
          <Row>
            <RowTitle width={100}>
              {`商品報價(${currencyType})`}
              <RequiredIcon>*</RequiredIcon>
            </RowTitle>
            <RowContent width={400}>
              <Form.Item name="quotedPrice" rules={[{ required: true, message: "" }]}>
                <Input placeholder="驗證完成 SKU(品號)後才能輸入" disabled={!isEditMode && !checkedSku} />
              </Form.Item>
            </RowContent>
          </Row>
          <Row>
            <RowTitle width={100}>
              商品後扣(%)
              {!isPostDeductedByRetailer && <RequiredIcon>*</RequiredIcon>}
            </RowTitle>
            <RowContent width={400}>
              <Form.Item name="postDeductionRate" rules={[{ required: !isPostDeductedByRetailer, message: "" }]}>
                <InputNumber
                  addonAfter="%"
                  min={0}
                  max={100}
                  step={0.01}
                  precision={3}
                  disabled={isPostDeductedByRetailer}
                />
              </Form.Item>
            </RowContent>
          </Row>
          <Row>
            <RowTitle width={100}>箱入數</RowTitle>
            <RowContent width={400}>
              <Form.Item name="pcsPerCarton">
                <Input placeholder="驗證完成 SKU(品號)後才能輸入" disabled={!isEditMode && !checkedSku} />
              </Form.Item>
            </RowContent>
          </Row>
          <Row>
            <RowTitle width={100}>允收天數</RowTitle>
            <RowContent width={400}>
              <Form.Item name="canAcceptDays">
                <Input placeholder="驗證完成 SKU(品號)後才能輸入" disabled={!isEditMode && !checkedSku} />
              </Form.Item>
            </RowContent>
          </Row>
          <Row>
            <RowTitle width={100}>通路商品編號</RowTitle>
            <RowContent width={400}>
              <Form.Item name="skuPlatform">
                <Input placeholder="驗證完成 SKU(品號)後才能輸入" disabled={!isEditMode && !checkedSku} maxLength={32} />
              </Form.Item>
            </RowContent>
          </Row>
          <ButtonContainer>
            <CancelButton onClick={onClose}>取消</CancelButton>
            <Button type="primary" htmlType="submit">
              確定
            </Button>
          </ButtonContainer>
        </Form>
      </PopWrapper>
    </PopupBackground>
  );
};

export default SupplyChainProductPopup;
